import { UserButton } from "@clerk/clerk-react";
import { BRAND_PERSON_NAME, BRAND_SITE_URL } from "@/lib/brandConfig";
import { useSEO } from "@/hooks/useSEO";
import { useUserRole, type UserRole } from "@/lib/auth";
import { ADMIN_PAGE_CATALOG as C } from "@/lib/adminPageCatalog";
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  Command as CommandIcon,
  Compass,
  ExternalLink,
  Film,
  Filter,
  Flag,
  Gift,
  Globe,
  HeartPulse,
  Image,
  Layout,
  LayoutDashboard,
  LineChart,
  Target,
  Wallet,
  Lock,
  Mail,
  Megaphone,
  Menu,
  Network,
  Package,
  Plug,
  Repeat2,
  ServerCog,
  Shield,
  SlidersHorizontal,
  Sparkles,
  Users,
  Video,
  X,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { AdminCommandPalette, type PaletteItem } from "@/components/admin/AdminCommandPalette";

// ─── Types ────────────────────────────────────────────────────────────────────

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  requireRole?: UserRole;
};

type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
  requireRole?: UserRole;
};

type QuickNavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
};

// ─── Role helpers ─────────────────────────────────────────────────────────────

const ROLE_RANK: Record<UserRole, number> = { super_admin: 2, admin: 1, user: 0 };
const meetsRole = (current: UserRole, required?: UserRole) =>
  ROLE_RANK[current] >= ROLE_RANK[required ?? "admin"];

// ─── Nav definition ───────────────────────────────────────────────────────────

/**
 * Nav IA (una app, un dueño por función):
 *  - Resumen: pulse + mapa + dinero
 *  - Operación: mirar funnel/leads/fotos (no editar config)
 *  - Conversión: editar qué ve el visitante (home, CTA geo, overrides)
 *  - Sitio / Comercio / Sistema
 * Labels from ADMIN_PAGE_CATALOG so nav and page headers stay aligned.
 */
const navSections: NavSection[] = [
  {
    id: "overview",
    label: "Resumen",
    items: [
      { to: C.dashboard.path, label: C.dashboard.navLabel, icon: LayoutDashboard, end: true },
      { to: C.centro.path, label: C.centro.navLabel, icon: Compass },
      { to: C.revenue.path, label: C.revenue.navLabel, icon: LineChart, requireRole: "super_admin" },
      {
        to: "/admin/finanzas-personales",
        label: C["personal-finance"].navLabel,
        icon: Wallet,
        requireRole: "super_admin",
      },
      { to: C.investor.path, label: C.investor.navLabel, icon: Target, requireRole: "super_admin" },
    ],
  },
  {
    id: "ops",
    label: "Operación",
    requireRole: "super_admin",
    items: [
      { to: C["live-funnel"].path, label: C["live-funnel"].navLabel, icon: Activity, requireRole: "super_admin" },
      { to: C["checkout-funnel"].path, label: C["checkout-funnel"].navLabel, icon: Filter, requireRole: "super_admin" },
      { to: C["recovery-leads"].path, label: C["recovery-leads"].navLabel, icon: Mail, requireRole: "super_admin" },
      { to: C["image-metrics"].path, label: C["image-metrics"].navLabel, icon: Image, requireRole: "super_admin" },
      { to: C["isp-report"].path, label: C["isp-report"].navLabel, icon: Network, requireRole: "super_admin" },
      { to: C["kimi-patterns"].path, label: C["kimi-patterns"].navLabel, icon: Sparkles, requireRole: "super_admin" },
    ],
  },
  {
    id: "conversion",
    label: "Conversión",
    items: [
      { to: C["country-templates"].path, label: C["country-templates"].navLabel, icon: Globe },
      { to: C.wishlist.path, label: C.wishlist.navLabel, icon: Gift },
      { to: C.cta.path, label: C.cta.navLabel, icon: Megaphone },
    ],
  },
  {
    id: "site",
    label: "Sitio",
    items: [
      { to: C["site-settings"].path, label: C["site-settings"].navLabel, icon: Layout },
      { to: C.portfolio.path, label: C.portfolio.navLabel, icon: Image },
      { to: C.audiovisual.path, label: C.audiovisual.navLabel, icon: Video },
      { to: C["private-reels"].path, label: C["private-reels"].navLabel, icon: Film },
    ],
  },
  {
    id: "commerce",
    label: "Comercio",
    items: [
      { to: C.productos.path, label: C.productos.navLabel, icon: Package },
      { to: C.suscripciones.path, label: C.suscripciones.navLabel, icon: Repeat2, requireRole: "super_admin" },
      { to: C.invitaciones.path, label: C.invitaciones.navLabel, icon: Mail, requireRole: "super_admin" },
      { to: C["premium-invite"].path, label: C["premium-invite"].navLabel, icon: Megaphone, requireRole: "super_admin" },
      { to: C.memberships.path, label: C.memberships.navLabel, icon: Shield, requireRole: "super_admin" },
      { to: C.buyers.path, label: C.buyers.navLabel, icon: Users, requireRole: "super_admin" },
      { to: C["argen-links"].path, label: C["argen-links"].navLabel, icon: Flag },
    ],
  },
  {
    id: "system",
    label: "Sistema",
    requireRole: "super_admin",
    items: [
      { to: C.integrations.path, label: C.integrations.navLabel, icon: Plug, requireRole: "super_admin" },
      { to: C["config-health"].path, label: C["config-health"].navLabel, icon: HeartPulse, requireRole: "super_admin" },
      { to: C["worker-flags"].path, label: C["worker-flags"].navLabel, icon: Zap, requireRole: "super_admin" },
      { to: C["env-setup"].path, label: C["env-setup"].navLabel, icon: ServerCog, requireRole: "super_admin" },
      { to: C["stripe-disputes"].path, label: C["stripe-disputes"].navLabel, icon: LineChart, requireRole: "super_admin" },
      { to: C["sidebar-config"].path, label: C["sidebar-config"].navLabel, icon: SlidersHorizontal, requireRole: "super_admin" },
    ],
  },
];

// ─── SEO ──────────────────────────────────────────────────────────────────────

const ADMIN_SEO = {
  title: `Admin Dashboard — ${BRAND_PERSON_NAME}`,
  description: "Dashboard de administración (Vite SPA) — una sola app bajo /admin",
  keywords: "",
  ogImage: `${BRAND_SITE_URL}/og.png`,
  canonical: `${BRAND_SITE_URL}/admin`,
  robots: "noindex, nofollow" as const,
};

// ─── Constants ────────────────────────────────────────────────────────────────

// Sidebar widths — keep in sync with the CSS class strings below
const SIDEBAR_W_EXPANDED  = 220; // px — md:w-[220px]
const SIDEBAR_W_COLLAPSED =  56; // px — md:w-[56px]

const MOBILE_QUICK_NAV: QuickNavItem[] = [
  { to: C.dashboard.path, label: "Inicio", icon: LayoutDashboard, end: true },
  { to: C.buyers.path, label: "Compradores", icon: Users },
  { to: C.memberships.path, label: "Aprobar", icon: Shield },
  { to: C["kimi-patterns"].path, label: "Offer", icon: Sparkles },
  { to: C["live-funnel"].path, label: "Live", icon: Activity },
];

// ─── SidebarNavItem ───────────────────────────────────────────────────────────

function SidebarNavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { to, label, icon: Icon, end, requireRole } = item;

  // Shared inner content — icon box + optional label + optional lock
  const inner = ({ isActive }: { isActive: boolean }) => (
    <>
      {/* Icon box — 28×28, optical centering */}
      <span
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-colors duration-150",
          isActive
            ? "bg-white/[0.12]"
            : "bg-transparent group-hover:bg-white/[0.06]",
        )}
      >
        <Icon
          className={cn(
            "h-[15px] w-[15px] transition-colors duration-150",
            isActive ? "text-white" : "text-white/35 group-hover:text-white/65",
          )}
          strokeWidth={isActive ? 2 : 1.75}
        />
      </span>

      {/* Label — only when expanded */}
      {!collapsed && (
        <>
          <span
            className={cn(
              "flex-1 truncate text-[12.5px] leading-none transition-colors duration-150",
              isActive ? "text-white font-medium" : "text-white/50 group-hover:text-white/80 font-normal",
            )}
          >
            {label}
          </span>
          {requireRole === "super_admin" && (
            <Lock
              className="h-[11px] w-[11px] shrink-0 text-white/18 transition-colors group-hover:text-white/30"
              strokeWidth={2}
              aria-label="Solo super admin"
            />
          )}
        </>
      )}
    </>
  );

  // Row wrapper classes
  const rowCls = ({ isActive }: { isActive: boolean }) =>
    cn(
      // layout
      "group relative flex w-full items-center gap-2 rounded-lg transition-all duration-150 active:scale-[0.97]",
      // sizing — collapsed: 36px square pill; expanded: full-width with padding
      collapsed ? "h-9 justify-center px-0" : "h-9 px-2",
      // states
      isActive
        ? "bg-white/[0.09] ring-1 ring-inset ring-white/[0.08] shadow-[0_1px_2px_rgba(0,0,0,0.4)]"
        : "hover:bg-white/[0.05]",
    );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink to={to} end={end} onClick={onNavigate} className={rowCls}>
            {inner}
          </NavLink>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          sideOffset={8}
          className="rounded-lg border border-white/[0.08] bg-[#141416] px-2.5 py-1.5 text-xs font-medium text-white/80 shadow-xl"
        >
          {label}
          {requireRole === "super_admin" && (
            <span className="ml-1.5 text-white/30">(super admin)</span>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <NavLink to={to} end={end} onClick={onNavigate} className={rowCls}>
      {inner}
    </NavLink>
  );
}

// ─── NavLinks ─────────────────────────────────────────────────────────────────

function NavLinks({
  role,
  collapsed,
  onNavigate,
  hiddenForAdmin,
  grantedToAdmin,
}: {
  role: UserRole;
  collapsed: boolean;
  onNavigate?: () => void;
  hiddenForAdmin: Set<string>;
  grantedToAdmin: Set<string>;
}) {
  const isSuperAdmin = role === "super_admin";

  const visibleSections = navSections
    .filter((s) => meetsRole(role, s.requireRole))
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => {
        if (!meetsRole(role, i.requireRole) && !grantedToAdmin.has(i.to)) return false;
        if (!isSuperAdmin && hiddenForAdmin.has(i.to)) return false;
        return true;
      }),
    }))
    .filter((s) => s.items.length > 0);

  return (
    <div className="flex flex-col gap-1">
      {visibleSections.map((section) => (
        <div key={section.id}>
          {/* Section label — only expanded and more than one section */}
          {!collapsed && (
            <p className="mb-1 mt-3 first:mt-0 px-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/[0.22] select-none">
              {section.label}
            </p>
          )}
          {collapsed && <div className="mt-2 first:mt-0" />}

          {/* Items */}
          <div className="flex flex-col gap-px">
            {section.items.map((item) => (
              <SidebarNavItem
                key={item.to}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── SidebarInner ─────────────────────────────────────────────────────────────

function SidebarInner({
  role,
  collapsed,
  onNavigate,
  hiddenForAdmin,
  grantedToAdmin,
}: {
  role: UserRole;
  collapsed: boolean;
  onNavigate?: () => void;
  hiddenForAdmin: Set<string>;
  grantedToAdmin: Set<string>;
}) {
  const isSuperAdmin = role === "super_admin";

  return (
    <ScrollArea className="flex-1 min-h-0">
      <nav className={cn("py-3", collapsed ? "px-[10px]" : "px-3")}>
        {/* Super-admin badge */}
        {isSuperAdmin && (
          <div
            className={cn(
              "mb-3 flex items-center gap-2 rounded-lg border border-white/[0.07] bg-white/[0.04]",
              collapsed ? "justify-center px-0 py-2" : "px-2.5 py-2",
            )}
          >
            <Shield className="h-3.5 w-3.5 shrink-0 text-white/40" strokeWidth={1.75} />
            {!collapsed && (
              <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/30 select-none">
                Super admin
              </span>
            )}
          </div>
        )}

        <NavLinks
          role={role}
          collapsed={collapsed}
          onNavigate={onNavigate}
          hiddenForAdmin={hiddenForAdmin}
          grantedToAdmin={grantedToAdmin}
        />
      </nav>
    </ScrollArea>
  );
}

// ─── AdminLayout ──────────────────────────────────────────────────────────────

export function AdminLayout() {
  const role = useUserRole();
  const location = useLocation();
  const layoutRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const { getAuthHeaders } = useAdminAuth();

  useSEO(ADMIN_SEO);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== "undefined"
      ? localStorage.getItem("admin-sidebar-collapsed") === "true"
      : false,
  );

  const [hiddenForAdmin, setHiddenForAdmin] = useState<Set<string>>(new Set());
  const [grantedToAdmin, setGrantedToAdmin] = useState<Set<string>>(new Set());

  // Cmd+K / Ctrl+K palette — single entry point para saltar a cualquier página
  // del admin o servicio externo (Railway, Stripe, Cloudflare, Mux, etc).
  const [paletteOpen, setPaletteOpen] = useState(false);

  /** Items del palette: aplana navSections respetando rol + sidebar-config. */
  const paletteItems = useMemo<PaletteItem[]>(() => {
    const isSuperAdmin = role === "super_admin";
    const out: PaletteItem[] = [];
    for (const section of navSections) {
      if (!meetsRole(role, section.requireRole)) continue;
      for (const item of section.items) {
        if (!meetsRole(role, item.requireRole) && !grantedToAdmin.has(item.to)) continue;
        if (!isSuperAdmin && hiddenForAdmin.has(item.to)) continue;
        out.push({ ...item, section: section.label });
      }
    }
    return out;
  }, [role, grantedToAdmin, hiddenForAdmin]);

  const activeItem = useMemo(() => {
    const isSuperAdmin = role === "super_admin";
    for (const section of navSections) {
      if (!meetsRole(role, section.requireRole)) continue;
      for (const item of section.items) {
        if (!meetsRole(role, item.requireRole) && !grantedToAdmin.has(item.to)) continue;
        if (!isSuperAdmin && hiddenForAdmin.has(item.to)) continue;
        if (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)) {
          return item;
        }
      }
    }
    return null;
  }, [role, grantedToAdmin, hiddenForAdmin, location.pathname]);

  const visibleQuickNav = useMemo(() => {
    const isSuperAdmin = role === "super_admin";
    return MOBILE_QUICK_NAV.filter((item) => {
      const source = navSections.flatMap((s) => s.items).find((navItem) => navItem.to === item.to);
      if (!source) return true;
      if (!meetsRole(role, source.requireRole) && !grantedToAdmin.has(source.to)) return false;
      if (!isSuperAdmin && hiddenForAdmin.has(source.to)) return false;
      return true;
    });
  }, [role, grantedToAdmin, hiddenForAdmin]);

  const loadSidebarConfig = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/sidebar-config", { headers });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      setHiddenForAdmin(new Set<string>(data.config?.hiddenForAdmin ?? []));
      setGrantedToAdmin(new Set<string>(data.config?.grantedToAdmin ?? []));
    } catch { /* non-critical */ }
  }, [getAuthHeaders]);

  useEffect(() => { loadSidebarConfig(); }, [loadSidebarConfig]);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Hotkey global Cmd+K (Mac) / Ctrl+K (Win/Linux). También cierra el palette
  // si está abierto y se presiona ESC — ese caso ya lo cubre Radix Dialog.
  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== "k" && ev.key !== "K") return;
      if (!(ev.metaKey || ev.ctrlKey)) return;
      // No interferir cuando el foco está en un input/textarea que ya use Cmd+K
      // (no existe hoy, pero por defensa).
      ev.preventDefault();
      setPaletteOpen((v) => !v);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onEscape = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  // Sync CSS custom property --admin-sticky-h with real header height
  useEffect(() => {
    const root = layoutRef.current;
    const sticky = stickyHeaderRef.current;
    if (!root || !sticky) return;
    const sync = () =>
      root.style.setProperty(
        "--admin-sticky-h",
        `${Math.round(sticky.getBoundingClientRect().height * 1000) / 1000}px`,
      );
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(sticky);
    window.addEventListener("resize", sync);
    return () => { ro.disconnect(); window.removeEventListener("resize", sync); };
  }, []);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("admin-sidebar-collapsed", String(next));
  };

  // Derived sidebar width for the offset of the main content area
  const sidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;
  void sidebarW; // used implicitly via Tailwind classes

  return (
    <div className="dark">
      <TooltipProvider delayDuration={300}>
        <div
          ref={layoutRef}
          className="flex min-h-dvh flex-col bg-[#09090b] font-ios [--admin-sticky-h:56px]"
        >
          {/* ══════════════════════════════════════════════════════════════════
              TOP HEADER — 56px, sticky, glass
          ══════════════════════════════════════════════════════════════════ */}
          <div
            ref={stickyHeaderRef}
            className="sticky top-0 z-50 h-14 border-b border-white/[0.06] bg-[#09090b]/70 shadow-[0_8px_24px_rgba(0,0,0,0.35)] backdrop-blur-2xl"
          >
            <div className="flex h-full items-center justify-between gap-3 px-3 sm:px-4">

              {/* ── Left: hamburger (mobile) + logo ── */}
              <div className="flex items-center gap-2">
                {/* Hamburger — mobile only */}
                <button
                  type="button"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Abrir menú"
                  className="md:hidden flex h-9 w-9 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.04] text-white/50 transition-colors hover:bg-white/[0.08] hover:text-white/80 active:scale-95"
                >
                  <Menu className="h-[18px] w-[18px]" strokeWidth={1.75} />
                </button>

                {/* Logo link */}
                <Link to="/admin" className="group flex items-center gap-2.5" aria-label="Ir al dashboard">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.05] transition-colors group-hover:bg-white/[0.09]">
                    <LayoutDashboard
                      className="h-4 w-4 text-white/60 transition-colors group-hover:text-white/80"
                      strokeWidth={1.75}
                    />
                  </span>
                  <span className="hidden flex-col leading-none sm:flex">
                    <span className="text-[13px] font-semibold tracking-tight text-white/80 transition-colors group-hover:text-white/95">
                      Dashboard
                    </span>
                    <span className="text-[9px] font-medium uppercase tracking-wider text-white/30">
                      Vite · /admin
                    </span>
                  </span>
                </Link>
                {activeItem && (
                  <span className="hidden rounded-full border border-white/[0.08] bg-white/[0.04] px-2.5 py-1 text-[11px] font-medium text-white/60 sm:inline-flex">
                    {activeItem.label}
                  </span>
                )}
              </div>

              {/* ── Right: ⌘K + "Ver sitio" + UserButton ── */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPaletteOpen(true)}
                  aria-label="Abrir paleta de comandos (⌘K)"
                  title="Buscar páginas y servicios (⌘K)"
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-white/[0.07] bg-white/[0.03] px-2.5 text-[12px] text-white/45 transition-all hover:bg-white/[0.08] hover:text-white/80 active:scale-95"
                >
                  <CommandIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Buscar</span>
                  <kbd className="hidden rounded border border-white/[0.10] bg-white/[0.04] px-1 text-[10px] font-medium text-white/55 sm:inline-flex">
                    ⌘K
                  </kbd>
                </button>
                <a
                  href="/?geo_sim_boot=1"
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-violet-400/25 bg-violet-500/10 px-2.5 text-[12px] text-violet-200/80 transition-all hover:bg-violet-500/20 hover:text-violet-100"
                  title="GeoSim · simular visitante por país (super_admin / QA)"
                >
                  <Globe className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Simular visitante</span>
                </a>
                <a
                  href="/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-8 items-center gap-1.5 rounded-lg border border-white/[0.07] bg-white/[0.03] px-3 text-[12px] text-white/40 transition-all hover:bg-white/[0.07] hover:text-white/70"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" strokeWidth={1.75} />
                  <span className="hidden sm:inline">Ver sitio</span>
                </a>

                {/* UserButton — let Clerk control its own sizing */}
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.03]">
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{ elements: { avatarBox: "h-6 w-6" } }}
                  />
                </span>
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════════════
              BODY — sidebar + main
          ══════════════════════════════════════════════════════════════════ */}
          <div className="flex flex-1">

            {/* ── Mobile overlay ── */}
            {mobileOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] md:hidden animate-in fade-in duration-150"
                onClick={() => setMobileOpen(false)}
              />
            )}

            {/* ════════════════════════════════════════════════════════════════
                SIDEBAR
            ════════════════════════════════════════════════════════════════ */}
            <aside
              role="dialog"
              aria-modal={mobileOpen ? true : undefined}
              aria-label="Menú de administración"
              className={cn(
                // Base layout
                "flex flex-col",
                // Background + border — glass
                "border-r border-white/[0.06] bg-[#0c0c0e]",
                // ── Mobile: full-height overlay drawer ──
                "fixed top-0 z-50",
                "max-md:h-dvh max-md:pt-[env(safe-area-inset-top,0px)] max-md:box-border max-md:w-[min(86vw,300px)]",
                "max-md:transition-transform max-md:duration-300 max-md:ease-in-out",
                mobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
                // ── Desktop: sticky under header ──
                "md:sticky md:top-[var(--admin-sticky-h,56px)] md:z-10",
                "md:h-[calc(100dvh-var(--admin-sticky-h,56px))]",
                "md:overflow-hidden",
                // Desktop width with smooth transition
                "md:transition-[width] md:duration-200 md:ease-in-out",
                collapsed ? "md:w-[56px]" : "md:w-[220px]",
              )}
            >
              {/* ── Mobile: drawer header ──────────────────────────────────── */}
              <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/[0.06] px-3 md:hidden">
                <div className="flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.08] bg-white/[0.05]">
                    <LayoutDashboard className="h-4 w-4 text-white/60" strokeWidth={1.75} />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[13px] font-semibold leading-none text-white/80">Admin</span>
                    <span className="text-[10px] leading-none text-white/30">Administración</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  aria-label="Cerrar menú"
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.07] bg-white/[0.03] text-white/40 transition-colors hover:bg-white/[0.08] hover:text-white/70 active:scale-95"
                >
                  <X className="h-[15px] w-[15px]" strokeWidth={1.75} />
                </button>
              </div>

              {/* ── Desktop: collapse toggle strip ────────────────────────── */}
              <div className="hidden h-9 shrink-0 items-center border-b border-white/[0.06] md:flex"
                style={{ justifyContent: collapsed ? "center" : "flex-end", paddingInline: "8px" }}
              >
                <button
                  type="button"
                  onClick={toggleCollapse}
                  title={collapsed ? "Expandir sidebar" : "Colapsar sidebar"}
                  className="flex h-7 w-7 items-center justify-center rounded-md border border-white/[0.07] bg-white/[0.03] text-white/30 transition-all hover:bg-white/[0.08] hover:text-white/60 active:scale-95"
                >
                  {collapsed
                    ? <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                    : <ChevronLeft  className="h-3.5 w-3.5" strokeWidth={2} />}
                </button>
              </div>

              {/* ── Nav content ─────────────────────────────────────────────── */}
              <SidebarInner
                role={role}
                collapsed={collapsed}
                onNavigate={() => setMobileOpen(false)}
                hiddenForAdmin={hiddenForAdmin}
                grantedToAdmin={grantedToAdmin}
              />
            </aside>

            {/* ════════════════════════════════════════════════════════════════
                MAIN CONTENT
            ════════════════════════════════════════════════════════════════ */}
            <main className="min-w-0 flex-1 overflow-x-hidden">
              <div className="mx-auto w-full min-w-0 max-w-[1400px] px-4 py-6 pb-[max(5.5rem,env(safe-area-inset-bottom)+4.25rem)] sm:px-6 sm:py-8 sm:pb-[max(1.5rem,env(safe-area-inset-bottom)+0.5rem)] lg:px-10 lg:py-10">
                <Outlet />
              </div>
            </main>
          </div>
          <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-white/[0.08] bg-[#0a0a0c]/95 px-2 pb-[max(env(safe-area-inset-bottom),0.4rem)] pt-1.5 shadow-[0_-6px_24px_rgba(0,0,0,0.45)] backdrop-blur-xl md:hidden">
            <div className="mx-auto flex w-full max-w-[560px] items-center justify-between gap-1">
              {visibleQuickNav.map(({ to, label, icon: Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) =>
                    cn(
                      "group flex min-h-12 flex-1 flex-col items-center justify-center rounded-xl px-1 py-1.5 transition-colors",
                      isActive ? "bg-white/[0.10]" : "hover:bg-white/[0.05]",
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={cn(
                          "mb-0.5 h-4 w-4 transition-colors",
                          isActive ? "text-white" : "text-white/55 group-hover:text-white/75",
                        )}
                        strokeWidth={1.8}
                      />
                      <span
                        className={cn(
                          "text-[10px] leading-none",
                          isActive ? "font-semibold text-white" : "font-medium text-white/60 group-hover:text-white/80",
                        )}
                      >
                        {label}
                      </span>
                    </>
                  )}
                </NavLink>
              ))}
              <button
                type="button"
                aria-label="Abrir menú completo"
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(true)}
                className="group flex min-h-12 min-w-14 flex-col items-center justify-center rounded-xl px-1 py-1.5 text-white/65 transition-colors hover:bg-white/[0.05] hover:text-white/85"
              >
                <Menu className="mb-0.5 h-4 w-4" strokeWidth={1.8} />
                <span className="text-[10px] font-medium leading-none">Menú</span>
              </button>
            </div>
          </nav>

          {/* ⌘K palette — montado en el root para que Radix Portal use el body
              y la modal cubra todo el viewport incluido el sidebar. */}
          <AdminCommandPalette
            open={paletteOpen}
            onOpenChange={setPaletteOpen}
            items={paletteItems}
          />
        </div>
      </TooltipProvider>
    </div>
  );
}
